import { areDetailedCybusMappingEquals, Capability, type CybusPubSub, CybusPubSubType, type StatusType } from '../../../../../../domain'

import type { SingleSubscriptionsTypes } from '../../../../../../application'

import type { BackendDefinition, StatusSupportedType } from '../../../../../Connectware'
import { type Resource, TopicsMapper } from '../../../../mappers'
import { BaseSingleResourceSubscriptionStrategy, type SingleResourceBaseRequest } from '../Resource'
import { mapBaseMapping } from './Base'

const mapPubSub = (
    { topic, connection, endpoint }: BackendDefinition<'service-manager', 'MappingResourceConfigPubSub'>,
    topicMapper: TopicsMapper<'Mapping'>
): CybusPubSub => ({
    type: endpoint ? CybusPubSubType.ENDPOINT : CybusPubSubType.TOPIC,
    id: endpoint || topic,
    topics: topicMapper.mapAll(topic),
    externalBroker: connection || null,
    subscriptionErrors: new Set(),
})

export class DetailedMappingStrategy extends BaseSingleResourceSubscriptionStrategy<'Mapping', 'mapping'> {
    protected override readonly areEquals = areDetailedCybusMappingEquals

    protected override resourceCategory = 'Mapping' as const

    protected readonly notFoundMessage = 'Could not fetch mapping'

    protected readonly resourceStatusType: StatusSupportedType = 'mapping'

    protected readonly baseRequest: SingleResourceBaseRequest<'Mapping'> = {
        capability: Capability.MAPPING_READ,
        method: 'GET',
        path: '/api/v2/resources/+',
    }

    override readonly requiredPermissions = [Capability.MAPPING_READ]

    protected mapResource (mapping: Resource<'Mapping'>, status: StatusType): SingleSubscriptionsTypes['mapping'] {
        const topicMapper = TopicsMapper.create(mapping.category)
        return {
            ...mapBaseMapping(mapping),
            entries: mapping.resourceConfig.args.mappings.map(({ publish, subscribe, rules }) => ({
                subscribe: subscribe.map((s) => mapPubSub(s, topicMapper)),
                publish: mapPubSub(publish, topicMapper),
                rules: rules ? [...rules] : null,
                /**
                 * @todo map execution errors
                 * @see https://cybusio.atlassian.net/browse/CC-3231
                 */
                executionError: null,
            })),
            status,
        }
    }
}
